import $ from "jquery";

var jsonTypeStr = "application/json; charset=utf-8";

// App.js listens for this event to store the refreshed token in state.
export const TOKEN_REFRESHED_EVENT = "kword-token-refreshed";

// Only one silent refresh runs at a time. Concurrent 401 responses share the same result.
let pendingTokenRefresh = null;

// The newest token that a silent refresh returned. LawPage.js registers its DOM event handlers
// only one time, so these handlers keep the token object of the first render. Without this
// value, each of their requests sends the expired token, gets a 401 and starts a new refresh.
let latestRefreshedToken = null;

// Goes up by one at each interactive sign-in. A refresh or a request that started before the
// newest sign-in can be for the account of an earlier sign-in, so it must not store a token,
// start a refresh or retry.
let signInCount = 0;

// App.js calls this after each interactive sign-in. After it, a token from an earlier refresh,
// a running refresh and a request that started earlier cannot change the token or retry.
export const resetTokenRefresh = () => {
  signInCount += 1;
  latestRefreshedToken = null;
  pendingTokenRefresh = null;
};

// MSAL puts the object ID of the user in uniqueId and the tenant ID in tenantId.
const isSameUser = (tokenObject, otherTokenObject) =>
  Boolean(tokenObject?.uniqueId) &&
  tokenObject.uniqueId === otherTokenObject?.uniqueId &&
  tokenObject.tenantId === otherTokenObject?.tenantId;

// Returns the token object to send. When the caller holds a token object from before the last
// refresh, the refreshed token replaces it, but only for the same user.
const withLatestRefreshedToken = (tokenObject) =>
  isSameUser(latestRefreshedToken, tokenObject)
    ? Object.assign({}, tokenObject, latestRefreshedToken)
    : tokenObject;

// Opens a hidden dialog that asks AAD for a new access token without user interaction.
// The dialog reuses the existing AAD sign-in session, so no sign-in screen is shown.
// The refresh fails when AAD returns another account than the account of requestTokenObject,
// or when a new sign-in occurs first. Then the app does not store or send the returned token.
export const refreshAccessToken = (requestTokenObject) => {
  if (pendingTokenRefresh) {
    return pendingTokenRefresh;
  }

  const signInCountAtStart = signInCount;
  const tokenRefresh = new Promise((resolve, reject) => {
    const dialogUrl =
      location.protocol +
      "//" +
      location.hostname +
      (location.port ? ":" + location.port : "") +
      "/login.html?silent=1";

    Office.context.ui.displayDialogAsync(dialogUrl, { height: 1, width: 1, promptBeforeOpen: false }, (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(`${result.error.code} ${result.error.message}`));
        return;
      }

      const dialog = result.value;
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (res) => {
        const msg = JSON.parse(res.message);
        dialog.close();
        if (msg.status !== "success") {
          reject(new Error(msg.result));
        } else if (signInCount !== signInCountAtStart) {
          reject(new Error("A new sign-in occurred during the token refresh."));
        } else if (!isSameUser(msg.result, requestTokenObject)) {
          reject(new Error("The token refresh returned another account."));
        } else {
          latestRefreshedToken = msg.result;
          window.dispatchEvent(new CustomEvent(TOKEN_REFRESHED_EVENT, { detail: msg.result }));
          resolve(msg.result);
        }
      });
      dialog.addEventHandler(Office.EventType.DialogEventReceived, (event) => {
        reject(new Error("Token refresh dialog closed. Error code: " + event.error));
      });
    });
  }).finally(() => {
    // After a new sign-in, pendingTokenRefresh can hold a newer refresh.
    if (pendingTokenRefresh === tokenRefresh) {
      pendingTokenRefresh = null;
    }
  });

  pendingTokenRefresh = tokenRefresh;
  return tokenRefresh;
};

// Sends a POST request with the bearer token, or with the newest refreshed token of the same
// user. On a 401 response, it refreshes the token once and retries the request one time with
// the new token. A request that started before the newest sign-in does not refresh or retry.
const postWithAuth = (url, data, decodedTokenObject, dataType = "json") => {
  const signInCountAtRequest = signInCount;
  return new Promise((resolve, reject) => {
    const send = (tokenObject, allowRefresh) => {
      $.ajax({
        type: "POST",
        url: url,
        data: JSON.stringify(data),
        dataType: dataType,
        contentType: jsonTypeStr,
        accepts: jsonTypeStr,
        timeout: 60000, // 60 sec
        beforeSend: function (xhr) {
          if (tokenObject && tokenObject.accessToken) {
            xhr.setRequestHeader("Authorization", "Bearer " + tokenObject.accessToken);
          }
        },
      })
        .done(function (responseData) {
          resolve(responseData);
        })
        .fail(function (xhr) {
          if (xhr.status === 401 && allowRefresh && signInCount === signInCountAtRequest) {
            refreshAccessToken(tokenObject)
              .then((refreshedTokenObject) => {
                // A new sign-in can occur after the refresh ends and before this callback, and
                // concurrent requests share one refresh, so each request checks both again.
                if (signInCount !== signInCountAtRequest) {
                  throw new Error("A new sign-in occurred before the retry.");
                }
                if (!isSameUser(refreshedTokenObject, tokenObject)) {
                  throw new Error("The shared token refresh is for another account.");
                }
                send(Object.assign({}, tokenObject, refreshedTokenObject), false);
              })
              .catch((error) => {
                // The caller gets the original 401. Only the console gets the reason, because
                // an AAD error text can contain user data.
                console.error("The token refresh failed, so the request is not sent again.", error);
                reject(xhr);
              });
            return;
          }
          reject(xhr);
        });
    };
    send(withLatestRefreshedToken(decodedTokenObject), true);
  });
};

export const getWorkItemInfo = async (decodedTokenObject) => {
  const workItemId = await getWorkItemId();
  const url = APP_BackendUrl + "getworkitem";
  const data = {
    Id: workItemId,
    ActorUserId: decodedTokenObject.uniqueId,
  };
  return postWithAuth(url, data, decodedTokenObject).catch((err) => {
    console.error("getWorkItem fail", err);
    throw err;
  });
};

const getWorkItemId = async () => {
  let fileUrl = await Office.context.document.url;
  let index = fileUrl.search(".docx");
  return fileUrl.substring(index - 12, index);
};

export const getUserInfoByObjectId = async (ObjectId, decodedTokenObject) => {
  const url = APP_BackendUrl + "getuserbyobjectid";
  const data = {
    ObjectId: ObjectId,
    ActorUserId: ObjectId,
  };
  return postWithAuth(url, data, decodedTokenObject).catch((err) => {
    log("ERROR", "fail getting user by object id. objectId: " + ObjectId);
    console.log("fail getting user by object id. objectId: " + ObjectId);
    console.error("getUserInfoByObjectId fail", err);
    throw err;
  });
};

export const getUserInfoById = async (id, actorUserId, decodedTokenObject) => {
  const url = APP_BackendUrl + "getuserbyid";
  const data = {
    Id: id,
    ActorUserId: actorUserId,
  };
  return postWithAuth(url, data, decodedTokenObject).catch((err) => {
    log("ERROR", "fail getting user by id. Id: " + id);
    console.log("fail getting user by id. Id: " + id);
    console.error("getUserInfoById fail", err);
    throw err;
  });
};

export const getLawText = (userInfoObject, workItemObject, decodedTokenObject) => {
  const url = APP_BackendUrl + "getlegaldocument";
  const data = {
    DocumentId: workItemObject.DocumentId,
    Version: workItemObject.Version,
    Source: workItemObject.Source,
    ActorUserId: userInfoObject.Id,
    AddKarnovAttributes: true,
  };
  return postWithAuth(url, data, decodedTokenObject, "html").catch((err) => {
    console.error("getLawText fail", err);
    throw err;
  });
};

export const initNoteIds = (ExistingNotes, userInfoObject, workItemObject, decodedTokenObject) => {
  const url = APP_BackendUrl + "syncnoteids";
  const data = {
    WorkItemId: workItemObject.Id,
    ActorUserId: userInfoObject.Id,
    Source: workItemObject.Source,
    DocumentId: workItemObject.DocumentId,
    ExistingNotes: ExistingNotes,
  };
  return postWithAuth(url, data, decodedTokenObject).catch((err) => {
    console.error("initNoteIds fail", err);
    throw err;
  });
};

export const newNoteId = (containerId, lastNoteId, userInfoObject, workItemObject, decodedTokenObject) => {
  const url = APP_BackendUrl + "addnoteid";
  const data = {
    ContainerId: containerId,
    ActorUserId: userInfoObject.Id,
    WorkItemId: workItemObject.Id,
    Source: workItemObject.Source,
    DocumentId: workItemObject.DocumentId,
    InsertAfterId: lastNoteId,
  };
  return postWithAuth(url, data, decodedTokenObject).catch((err) => {
    console.error("newNoteId fail", err);
    throw err;
  });
};

export const removeNoteId = (containerId, noteId, userInfoObject, workItemObject, decodedTokenObject) => {
  const url = APP_BackendUrl + "removenoteid";
  const data = {
    ContainerId: containerId,
    WorkItemId: workItemObject.Id,
    ActorUserId: userInfoObject.Id,
    NoteId: noteId,
  };
  return postWithAuth(url, data, decodedTokenObject).catch((err) => {
    console.error("removeNoteId fail", err);
    throw err;
  });
};

export const validateNewNoteIdDocOpsMode = (
  containerId,
  inputNodeId,
  userInfoObject,
  workItemObject,
  decodedTokenObject
) => {
  const url = APP_BackendUrl + "validateexistingnoteid";
  const noteIdStr = "n" + inputNodeId;
  const data = {
    ActorUserId: userInfoObject.Id,
    Source: workItemObject.Source,
    DocumentId: workItemObject.DocumentId,
    ContainerId: containerId,
    NoteIds: [noteIdStr],
  };
  return postWithAuth(url, data, decodedTokenObject).catch((err) => {
    console.error("newNoteId DocOpsMode fail", err);
    throw err;
  });
};

export const getChangesSummaries = async (workItemId, decodedTokenObject) => {
  const url = APP_BackendUrl + "changesummaries";
  const data = {
    WorkItemId: workItemId,
    ActorUserId: decodedTokenObject.uniqueId,
  };
  return postWithAuth(url, data, decodedTokenObject).catch((err) => {
    console.error("getChangesSummaries fail", err);
    throw err;
  });
};

export const getLinks = (text, userInfoObject, workItemObject, decodedTokenObject) => {
  let convertedText = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
  convertedText = convertedText.replace(/[^\S\n]/g, " ");
  convertedText = convertedText.replace(/[“”«»]+/g, '"');

  const url = APP_BackendUrl + "getlinks";
  const data = {
    Text: convertedText,
    ActorUserId: userInfoObject.Id,
    Source: workItemObject.Source,
    DocumentId: workItemObject.DocumentId,
    WorkItemId: workItemObject.Id,
  };
  return postWithAuth(url, data, decodedTokenObject).catch((err) => {
    console.error("Linker fail", err);
    throw err;
  });
};

export const enableOnRevision = (
  contentReviewer,
  euReviewer,
  proofReader,
  userInfoObject,
  workItemObject,
  decodedTokenObject
) => {
  const url = APP_BackendUrl + "updateworkitem";
  const data = {
    Id: workItemObject.Id,
    ActorUserId: userInfoObject.Id,
    Delta: {
      OmitNotifications: true,
    },
  };
  if (contentReviewer) data.EnableOnRevisionContentReviewer = true;
  if (proofReader) data.EnableOnRevisionProofReader = true;
  if (euReviewer) data.EnableOnRevisionEUReviewer = true;
  return postWithAuth(url, data, decodedTokenObject).catch((err) => {
    console.error("enableOnRevision fail", err);
    throw err;
  });
};

export const setWorkItemStatus = (
  newStatus,
  omitNotification = false,
  userInfoObject,
  workItemObject,
  decodedTokenObject
) => {
  const url = APP_BackendUrl + "setstatusonworkitem";
  const data = {
    Id: workItemObject.Id,
    ActorUserId: userInfoObject.Id,
    Status: newStatus,
    OmitNotification: omitNotification,
  };
  return postWithAuth(url, data, decodedTokenObject).catch((err) => {
    console.error("setWorkItemStatus fail", err);
    throw err;
  });
};

export const ping = (senderId, receiverId, workItemObject, userInfoObject, decodedTokenObject) => {
  const url = APP_BackendUrl + "ping";
  const data = {
    Id: workItemObject.Id,
    SenderId: senderId,
    ReceiverId: receiverId,
    ActorUserId: userInfoObject.Id,
  };
  return postWithAuth(url, data, decodedTokenObject).catch((err) => {
    console.error("ping fail", err);
    throw err;
  });
};

export const reviewApproval = (type, workItemObject, userInfoObject, decodedTokenObject) => {
  const url = APP_BackendUrl + "setreviewstatusonworkitem";
  const data = {
    Id: workItemObject.Id,
    ReviewStatus: "Approved",
    ReviewTypes: [type],
    ActorUserId: userInfoObject.Id,
  };
  return postWithAuth(url, data, decodedTokenObject).catch((err) => {
    console.error("reviewApproval fail", err);
    throw err;
  });
};

export const docOpsPublisering = (userInfoObject, workItemObject, decodedTokenObject) => {
  const url = APP_BackendUrl + "publishversionmetadata";
  const data = {
    ActorUserId: userInfoObject.Id,
    Source: workItemObject.Source,
    DocumentId: workItemObject.DocumentId,
    Version: workItemObject.Version,
  };
  return postWithAuth(url, data, decodedTokenObject).catch((err) => {
    console.error("docOpsPublisering fail", err);
    throw err;
  });
};

export const log = (level, message, method, params, workItemObject, userInfoObject, decodedTokenObject) => {
  return new Promise(async function (resolve, reject) {
    const url = APP_BackendUrl + "log";
    const data = {
      Level: level ?? "",
      Message: message ?? "",
      Method: method ?? "",
      Parameters: params ?? "",
      // Sessionid: this.sessionId,
      WorkItemId: workItemObject.Id ?? "",
      // Version: kWordGitVersion,
      UserAgent: getUserAgent(),
      Platform: Office.context.platform ?? "unknown platform",
      UserId: userInfoObject.Id ?? "",
      UserName: userInfoObject.Name ?? "",
      UserRole: getRoleFromUserInfo(userInfoObject),
    };
    const json = JSON.stringify(data);
    $.ajax({
      type: "POST",
      url: url,
      data: json,
      contentType: jsonTypeStr,
      accepts: jsonTypeStr,
      timeout: 60000, // 60 sec
      beforeSend: function (xhr) {
        //Include the bearer token in header
        xhr.setRequestHeader("Authorization", "Bearer " + decodedTokenObject.accessToken);
      },
    })
      .done(function (data) {
        resolve(data);
      })
      .fail(function (err) {
        reject(console.error("send log message fail", err));
      });
  });
};

export const logMetric = (
  category,
  subCategory,
  status,
  tag,
  message,
  workItemObject,
  userInfoObject,
  decodedTokenObject
) => {
  return new Promise(async function (resolve, reject) {
    const url = APP_BackendUrl + "logmetric";
    const data = {
      Category: category ?? "",
      SubCategory: subCategory ?? "",
      WorkItemId: workItemObject.Id ?? "",
      Tag: tag ?? "",
      Status: status ?? "",
      Message: message ?? "",
      // Payload: "payload",
      UserAgent: getUserAgent(),
      Platform: Office.context.platform ?? "unknown platform",
      UserId: userInfoObject.Id ?? "",
      UserName: userInfoObject.Name ?? "",
      UserRole: getRoleFromUserInfo(userInfoObject),
    };
    const json = JSON.stringify(data);
    $.ajax({
      type: "POST",
      url: url,
      data: json,
      contentType: jsonTypeStr,
      accepts: jsonTypeStr,
      timeout: 60000, // 60 sec
      beforeSend: function (xhr) {
        //Include the bearer token in header
        xhr.setRequestHeader("Authorization", "Bearer " + decodedTokenObject.accessToken);
      },
    })
      .done(function (data) {
        resolve(data);
      })
      .fail(function (err) {
        reject(console.error("send Metric message fail", err));
      });
  });
};

const getUserAgent = () => {
  if (window.navigator && window.navigator.userAgent) {
    return window.navigator.userAgent;
  }
  return "unknown user agent";
};

const getRoleFromUserInfo = (userInfoObject) => {
  if (userInfoObject.IsKarnovDevOps) {
    return "KarnovDevOps";
  } else if (userInfoObject.IsKarnovContentEditor) {
    return "KarnovContentEditor";
  } else if (userInfoObject.IsKarnovEmployee) {
    return "KarnovEmployee";
  } else if (userInfoObject.IsEUEditor) {
    return "EUEditor";
  } else if (userInfoObject.IsKarnovUser) {
    return "KarnovUser";
  }
  return "unknown role";
};
